import React, { memo, useEffect, useMemo, useState } from 'react';
import { ListItemText, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { isEmpty } from 'lodash';
import { User } from 'common/types/user';
import PerfectScrollbar from 'components/SafePerfectScrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { useManageEmails } from 'pages/Portfolio/hooks/useManageEmails';
import { useCompanyPermissions } from 'services/hooks/useCompanyPermissions';
import { useDeleteCompanyUserPermissions } from 'services/hooks/useManageUser/useManageUser';
import CompanyUserChips from './CompanyUserChips';
import CompanyUserInput from './CompanyUserInput';
import { CompanyUsersTableProps, CompanyUsersTableRowProps } from './types';

const cellStyle = { padding: '4px', width: '20%' };

const CompanyUsersTableRow = memo(({ company, emailHandlers }: CompanyUsersTableRowProps) => {
  const { inputValue, emails, handleEmailChange, handleAddEmail, handleDeleteEmail } = emailHandlers;
  const { mutate: removePermissions } = useDeleteCompanyUserPermissions();

  const firmUsers = useMemo(() => {
    if (company.company_id) {
      const users = emails[company.company_id]?.map(email => ({ email } as User));
      return users?.filter(user => user.email);
    }
    return [];
  }, [emails, company.company_id]);
  const [companyUsers, setCompanyUsers] = useState(company.users);

  // The permission query resolves after rows mount, so keep the editable chip state in sync with fresh row data.
  useEffect(() => {
    setCompanyUsers(company.users);
  }, [company.users]);

  const handleRemovePermissions = (userId: number, companyId: number) => {
    removePermissions({ userId, companyId });
    setCompanyUsers(prev => prev?.filter(user => user.id !== userId));
  };

  return (
    <TableRow key={company.company_id} style={{ verticalAlign: 'top' }}>
      <TableCell style={cellStyle}>
        <ListItemText primary={company.company_name} primaryTypographyProps={{ variant: 'body2' }} />
      </TableCell>
      <TableCell style={cellStyle}>
        <CompanyUserInput
          companyId={company.company_id}
          firmUsersEmails={company.usersNotInCompany?.map(user => user.email).filter(Boolean) ?? []}
          inputValue={inputValue[company.company_id] ?? ''}
          onInputChange={handleEmailChange}
          onAddEmail={handleAddEmail}
        />
      </TableCell>
      <TableCell style={cellStyle}>
        <CompanyUserChips
          users={firmUsers}
          onDelete={user => (user.email ? handleDeleteEmail(company.company_id, user.email) : undefined)}
        />
      </TableCell>
      <TableCell style={cellStyle}>
        {isEmpty(companyUsers) ? (
          <div>No users found</div>
        ) : (
          <CompanyUserChips
            users={companyUsers ?? []}
            onDelete={user => (user.id ? handleRemovePermissions(user.id, company.company_id) : undefined)}
          />
        )}
      </TableCell>
    </TableRow>
  );
});

const CompanyUsersTable = ({ filteredCompanies, firmUsers, emails, setEmails, firmId }: CompanyUsersTableProps) => {
  const { companiesWithPermissions, loading } = useCompanyPermissions(firmId, filteredCompanies, firmUsers);
  const emailHandlers = useManageEmails({ emails, setEmails });

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <PerfectScrollbar style={{ maxHeight: '40vh', overflowY: 'auto' }}>
      <TableContainer style={{ minWidth: '40rem' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Company Name</TableCell>
              <TableCell>Add User</TableCell>
              <TableCell>Pending Users</TableCell>
              <TableCell>Existing Users</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companiesWithPermissions.map(company => (
              <CompanyUsersTableRow key={company.company_id} company={company} emailHandlers={emailHandlers} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </PerfectScrollbar>
  );
};

export default CompanyUsersTable;
